import { Components, Theme } from '@mui/material/styles';

/**
 * Component-level customizations
 *
 * Override default props and styles for MUI components.
 * This ensures consistent behavior and appearance across the app.
 */
export const components: Components<Theme> = {
  // Button customizations
  MuiButton: {
    defaultProps: {
      disableElevation: true,  // Flat buttons (no shadow) by default
      variant: 'contained',     // Default to contained buttons
    },
    styleOverrides: {
      root: {
        borderRadius: 8,         // Rounded corners
        padding: '8px 16px',
        fontWeight: 500,
        textTransform: 'none',   // Don't force uppercase
      },
      sizeLarge: {
        padding: '12px 24px',
        fontSize: '1rem',
      },
      sizeSmall: {
        padding: '6px 12px',
        fontSize: '0.8125rem',
      },
    },
  },

  // TextField/Input customizations
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',      // Default to outlined style
      size: 'medium',
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,         // Rounded corners for inputs
      },
    },
  },

  // Card customizations
  MuiCard: {
    defaultProps: {
      elevation: 0,             // Flat cards (no shadow)
    },
    styleOverrides: {
      root: {
        borderRadius: 12,        // More rounded corners for cards
        border: '1px solid rgba(0, 0, 0, 0.08)',  // Subtle border
      },
    },
  },

  // Paper customizations (used for dialogs, menus, etc.)
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
      elevation1: {
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
      },
      elevation2: {
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12)',
      },
      elevation3: {
        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.16)',
      },
    },
  },

  // Dialog customizations
  MuiDialog: {
    defaultProps: {
      maxWidth: 'sm',
    },
    styleOverrides: {
      paper: {
        borderRadius: 16,
      },
    },
  },

  // Chip customizations
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
      },
    },
  },

  // AppBar customizations
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
  },

  // Drawer customizations
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,  // Drawers shouldn't have rounded corners
      },
    },
  },

  // Table customizations
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      },
      head: {
        fontWeight: 600,
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      },
    },
  },

  // DataGrid customizations (will be applied when using DataGrid components)
  // Note: MuiDataGrid is part of @mui/x-data-grid, configured separately if needed

  // Tab customizations
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',   // Don't force uppercase on tabs
        fontWeight: 500,
        minHeight: 48,
      },
    },
  },

  // Switch customizations
  MuiSwitch: {
    styleOverrides: {
      root: {
        padding: 8,
      },
      thumb: {
        boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
      },
    },
  },

  // Tooltip customizations
  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.87)',
        fontSize: '0.75rem',
        padding: '8px 12px',
        borderRadius: 6,
      },
      arrow: {
        color: 'rgba(0, 0, 0, 0.87)',
      },
    },
  },
};
